import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { appNavigation, urlMap } from '../../utils';
import { getWebLanguage, tWeb } from '../../i18n/sharedTranslations';
import Footer from '../components/Footer';

class Register extends Component {
  t = (key, vars) => tWeb(getWebLanguage(), key, vars);

  state = {
    name: '',
    email: '',
    password: '',
    repeatPassword: '',
    accountType: 'user',
    publisherBio: '',
    acceptedLegal: false,
    marketingOptIn: false,
    error: '',
    success: '',
    resendNotice: '',
    adminNotice: '',
    loading: false,
    adminLoading: false,
  };

  handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value,
      error: '',
      success: '',
      resendNotice: '',
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, password, repeatPassword, accountType, publisherBio, acceptedLegal, marketingOptIn } = this.state;

    if (!acceptedLegal) {
      this.setState({ error: 'You must accept Terms of Service and Privacy Policy to continue.' });
      return;
    }

    if (password !== repeatPassword) {
      this.setState({ error: 'Passwords do not match' });
      return;
    }

    if (accountType === 'publisher' && !String(publisherBio || '').trim()) {
      this.setState({ error: 'Publisher bio is required when registering as a publisher.' });
      return;
    }

    try {
      const response = await fetch(urlMap.register, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          password,
          accountType,
          marketingOptIn,
          bio: accountType === 'publisher' ? publisherBio : undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      this.setState({
        name: '',
        email,
        password: '',
        repeatPassword: '',
        accountType: 'user',
        publisherBio: '',
        marketingOptIn: false,
        error: '',
        success: data?.message || (accountType === 'publisher'
          ? 'Publisher account request created. Verify your email first, then wait for admin approval before logging in as publisher.'
          : 'Account created! Please check your email and verify your account before logging in.'),
      });
      // One-time onboarding hint consumed in ProjectPage after first login.
      localStorage.setItem('storyclub.preselectAgentMenu', '1');
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  handleResend = async () => {
    this.setState({ loading: true, resendNotice: '' });
    try {
      const response = await fetch(urlMap.resendVerification, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: this.state.email }),
      });

      if (!response.ok) {
        throw new Error('Failed to resend verification email');
      }

      this.setState({
        resendNotice: '✅ A new verification email has been sent. Please check your inbox.',
        loading: false,
      });
    } catch (err) {
      this.setState({
        resendNotice: '❌ Could not resend verification email. Try again later.',
        loading: false,
      });
    }
  };

  handleAdminVerification = async () => {
    this.setState({ adminLoading: true, adminNotice: '' });
    try {
      const response = await fetch(urlMap.requestAdminVerification, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: this.state.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }

      this.setState({
        adminNotice: '✅ ' + data.message,
        adminLoading: false,
      });
    } catch (err) {
      this.setState({
        adminNotice: '❌ Could not send admin verification request. Try again later.',
        adminLoading: false,
      });
    }
  };

  render() {
    const {
      email,
      name,
      password,
      repeatPassword,
      accountType,
      publisherBio,
      acceptedLegal,
      marketingOptIn,
      error,
      success,
      resendNotice,
      adminNotice,
      loading,
      adminLoading,
    } = this.state;

    return (
      <div className="login-page">
        <div className="login-wrapper">
          {!success ? (
            <form className="login-form" onSubmit={this.handleSubmit}>
              <h2>Create Account</h2>
              <p className="subtitle">Join Storyclub today</p>

              <input
                type="text"
                name="name"
                placeholder="Name"
                value={name}
                onChange={this.handleChange}
                required
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={email}
                onChange={this.handleChange}
                required
              />

              <label className="register-field" htmlFor="accountType">
                <span className="register-field-label">{this.t('register.accountType')}</span>
                <select
                  id="accountType"
                  className="register-select"
                  name="accountType"
                  value={accountType}
                  onChange={this.handleChange}
                  required
                >
                  <option value="user">{this.t('register.roleUser')}</option>
                  <option value="publisher">{this.t('register.rolePublisher')}</option>
                </select>
              </label>

              {accountType === 'publisher' ? (
                <label className="register-field" htmlFor="publisherBio">
                  <span className="register-field-label">{this.t('register.publisherBio')}</span>
                  <textarea
                    id="publisherBio"
                    className="register-textarea"
                    name="publisherBio"
                    placeholder={this.t('register.publisherBioPlaceholder')}
                    value={publisherBio}
                    onChange={this.handleChange}
                    rows={4}
                    required
                  />
                  <span className="register-field-hint">
                    {this.t('register.publisherBioHint')}
                  </span>
                </label>
              ) : null}

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={password}
                onChange={this.handleChange}
                required
              />

              <input
                type="password"
                name="repeatPassword"
                placeholder="Repeat Password"
                value={repeatPassword}
                onChange={this.handleChange}
                required
              />

              {error && <p className="error-message">{error}</p>}

              <label className="legal-checkbox-row" htmlFor="acceptedLegal">
                <input
                  id="acceptedLegal"
                  name="acceptedLegal"
                  type="checkbox"
                  checked={acceptedLegal}
                  onChange={this.handleChange}
                />
                <span>
                  I have read and agree to the{' '}
                  <a href="https://storyclub.ai/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                  and{' '}
                  <a href="https://storyclub.ai/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </span>
              </label>

              <label className="legal-checkbox-row" htmlFor="marketingOptIn">
                <input
                  id="marketingOptIn"
                  name="marketingOptIn"
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={this.handleChange}
                />
                <span>I want to receive product updates and marketing emails.</span>
              </label>

              <button type="submit">Register</button>

              <p className="footer-text">
                Already have an account? <Link to={appNavigation.login}>Login</Link>
              </p>
              <p className="footer-text">
                <Link to="/">Back to Home</Link>
              </p>
            </form>
          ) : (
            <div className="login-form">
              <h2>Welcome to Storyclub</h2>
              <p className="success-message">{success}</p>

              <p className="info-message" style={{ marginTop: '12px' }}>
                Didn't receive the email? Check your <strong>spam</strong>, <strong>junk</strong>, or <strong>trash</strong> folder.
                If you still can't find it, resend the email or request manual verification from an admin.
              </p>

              {resendNotice && <p className="info-message">{resendNotice}</p>}

              <button
                onClick={this.handleResend}
                disabled={loading}
                className="resend-btn"
                style={{ marginTop: '16px' }}
              >
                {loading ? 'Resending…' : 'Resend verification email'}
              </button>

              {adminNotice && <p className="info-message">{adminNotice}</p>}

              <button
                onClick={this.handleAdminVerification}
                disabled={adminLoading}
                className="resend-btn"
                style={{ marginTop: '8px' }}
              >
                {adminLoading ? 'Requesting…' : 'Verify via admin'}
              </button>

              <p className="footer-text">
                Already verified? <Link to={appNavigation.login}>Login</Link>
              </p>
            </div>
          )}
        </div>

        <Footer />
      </div>
    );
  }
}

export default Register;
